 import React, { useEffect, useState } from "react";
 import Select from "react-select";
 import { useParams, useLocation, useNavigate } from "react-router-dom";
 import api from "../api/api";
 import { CheckIcon } from "@heroicons/react/24/solid";
 import * as XLSX from 'xlsx';
 
 // ====================================================================
 // FUNCTION TO PARSE EXCEL DATA
 // ====================================================================
 const parseExcelFile = (file, index, parts) => {
   return new Promise((resolve, reject) => {
     const reader = new FileReader();
 
     reader.onload = (e) => {
       try {
         const data = new Uint8Array(e.target.result);
         const workbook = XLSX.read(data, { type: 'array', cellDates: true });
         const sheetName = workbook.SheetNames[0];
         const worksheet = workbook.Sheets[sheetName];
 
         const allCells = Object.keys(worksheet)
           .filter((k) => k[0] !== '!')
           .reduce((acc, key) => {
             acc[key] = String(worksheet[key].v).trim();
             return acc;
           }, {});
 
         const findCellByKeyword = (keyword) =>
           Object.entries(allCells).find(([cell, val]) =>
             val.toLowerCase().includes(keyword.toLowerCase())
           );
 
         const getValueRight = (cellKey) => {
           if (!cellKey) return '';
           const match = cellKey.match(/([A-Z]+)(\d+)/);
           if (!match) return '';
           let [_, col, row] = match;
           const rowNum = parseInt(row);
           let nextColCode = col.charCodeAt(0) + 1;
           while (nextColCode <= 90) {
             const nextKey = `${String.fromCharCode(nextColCode)}${rowNum}`;
             if (worksheet[nextKey]?.v) return String(worksheet[nextKey].v).trim();
             nextColCode++;
           }
           return '';
         };
 
         const extractNumber = (val) => {
           if (!val) return 0;
           const num = val.toString().replace(/[^\d.-]/g, '');
           return num ? parseFloat(num) : 0;
         };
 
         const partNoCell = findCellByKeyword('part no');
         const lotQtyCell = findCellByKeyword('lot qty');
         const dateCell = findCellByKeyword('date');
         const partNameCell = findCellByKeyword('partname');
         const batchNoCell = findCellByKeyword('batch no');
         const inspQtyCell = findCellByKeyword('inspection qty');
         const rejectQtyCell = findCellByKeyword('rejection qty');
 
         const headData = {
           PartNo: getValueRight(partNoCell?.[0]) || '',
           PartName: getValueRight(partNameCell?.[0]) || '',
           BatchNo: getValueRight(batchNoCell?.[0]) || '',
           LotQty: extractNumber(getValueRight(lotQtyCell?.[0])),
           InspectionQty: extractNumber(getValueRight(inspQtyCell?.[0])),
           RejectionQty: extractNumber(getValueRight(rejectQtyCell?.[0])),
           GrnDate: (() => {
             const raw = getValueRight(dateCell?.[0]);
             if (!raw) return new Date().toISOString().split('T')[0];
             const parsed = new Date(raw);
             if (!isNaN(parsed)) return parsed.toISOString().split('T')[0];
             const parts = raw.split(/[-/]/);
             if (parts.length === 3) {
               const [d, m, y] = parts.map((p) => parseInt(p, 10));
               return new Date(y, m - 1, d).toISOString().split('T')[0];
             }
             return new Date().toISOString().split('T')[0];
           })(),
         };
 
         console.log("✅ Parsed Header Data:", headData);
 
         const customHeaders = [
           "SlNo", "ItemSpec", "Description", "LowerLimit", "UpperLimit",
           "Obs1", "Obs2", "Obs3", "Obs4", "Obs5", "Obs6", "Obs7", "Obs8", "Obs9", "Obs10",
           "Result", "Remark", "GipSeqid"
         ];
 
         const rawItemRows = XLSX.utils.sheet_to_json(worksheet, {
           header: customHeaders,
           range: 8,
           defval: ''
         });
 
         const itemData = rawItemRows
           .filter(row => {
             const spec = String(row.ItemSpec).trim();
             const desc = String(row.Description).trim();
             return (
               spec !== '' &&
               !spec.toLowerCase().includes('approved') &&
               !desc.toLowerCase().includes('approved')
             );
           })
           .map((row, i) => {
             console.log('Debug: parts is:', parts);
             console.log('Debug: index is:', index);
             console.log('Debug: row:', row);
             console.log('Debug: i:', i);
             return {
               ItemID: parts[index].Itemid,
               SlNo: row.SlNo || i + 1,
               ItemSpec: String(row.ItemSpec).trim(),
               Description: String(row.Description).trim(),
               LowerLimit: String(row.LowerLimit).trim(),
               UpperLimit: String(row.UpperLimit).trim(),
               Obs1: String(row.Obs1).trim(),
               Obs2: String(row.Obs2).trim(),
               Obs3: String(row.Obs3).trim(),
               Obs4: String(row.Obs4).trim(),
               Obs5: String(row.Obs5).trim(),
               Obs6: String(row.Obs6).trim(),
               Obs7: String(row.Obs7).trim(),
               Obs8: String(row.Obs8).trim(),
               Obs9: String(row.Obs9).trim(),
               Obs10: String(row.Obs10).trim(),
               Result: String(row.Result).trim(),
               Remark: String(row.Remark).trim(),
               GipSeqid: parts[index].QcSeqId,
             }
           });
 
         console.log("✅ Parsed Item Data:", itemData);
 
         resolve({ headData, itemData });
       } catch (error) {
         console.error("❌ Error parsing Excel file:", error);
         reject(new Error(`Error parsing Excel file: ${error.message}`));
       }
     };
 
     reader.onerror = (error) =>
       reject(new Error(`File read error: ${error.message}`));
 
     reader.readAsArrayBuffer(file);
   });
 };
 
 export default function EditQC() {
   const { gipNo } = useParams();
   const location = useLocation();
   const navigate = useNavigate();
   const rowData = location.state;
   const Gipid = rowData?.Gipid || gipNo;
 
   const [loading, setLoading] = useState(false);
   const [record, setRecord] = useState({});
   const [parts, setParts] = useState([]);
   const [users, setUsers] = useState([]); // CheckedBy dropdown (name or id)
   const [supervisors, setSupervisors] = useState([]); // Supervisor dropdown
   const [qcNo, setQcNo] = useState("");
   const [qcDate, setQcDate] = useState("");
   const [preparedBy, setPreparedBy] = useState(null);
   const [checkedBy, setCheckedBy] = useState(null);
   const [supervisor, setSupervisor] = useState(null);
 
   // asset popup state
   const [showPopup, setShowPopup] = useState(false);
   const [selectedPartIndex, setSelectedPartIndex] = useState(null);
   const [selectedAsset, setSelectedAsset] = useState(null);
   const [selectedMandrel, setSelectedMandrel] = useState(null);
   const [selectedDie, setSelectedDie] = useState(null);
   const [assetOptions, setAssetOptions] = useState([]);
   const [mandrelOptions, setMandrelOptions] = useState([]);
   const [dieOptions, setDieOptions] = useState([]);
 
   // rejection popup state
   const [showRejPopup, setShowRejPopup] = useState(false);
   const [rejIndex, setRejIndex] = useState(null);
   const [rejType, setRejType] = useState(null);
   const [rejQty, setRejQty] = useState("");
   const [rejRemarks, setRejRemarks] = useState("");
   const [rejRows, setRejRows] = useState([]);
   const [qcRejQtyLimit, setQcRejQtyLimit] = useState(0);
   const [rejectionTypesOptions, setRejectionTypesOptions] = useState([]);
   // attachment modal state
   const [attachmentModalState, setAttachmentModalState] = useState({});
 
   // file read state
   const [files, setFiles] = useState({});
   // Table headers (labels) and mapping to part keys
   const headers = [
     "QcId",
     "Gipid",
     "QcSeqId",
     "Sl No",
     "PO No",
     "PoId",
     "PO Date",
     "itemslno",
     "Itemid",
     "Part No",
     "Description",
     "UOM",
     "Materialtype",
     "Mc No",
     "HeatNo",
     "Received",
     "Accepted",
     "Rej Qty",
     // "Download",
     "Download",
     "Asset Info",
     "Rejection Details",
     "UnitPrice",
     "Amount",
     
   ];
 
   // map header label -> parts[] property name (accessor)
   const headerMap = {
     "QcId": "QcId",
     "Gipid": "Gipid",
     "QcSeqId": "QcSeqId",
     "Sl No": "slno",
     "PO No": "poNo",
     "PoId": "PoId",
     "PO Date": "poDate",
     "itemslno": "itemslno",
     "Itemid": "Itemid",
     "Part No": "partNo",
     "Description": "description",
     "UOM": "uom",
     "Materialtype": "Materialtype",
     "Mc No": "mcNo",
     "HeatNo": "HeatNo",
     "Received": "receivedQty",
     "Accepted": "acceptedQty",
     "Rej Qty": "qcRejQty",
     // "Download": "uploadedFile",
     "Asset Info": "assetInfo",
     "Rejection Details": "rejectionDetails",
     "UnitPrice": "UnitPrice",
     "Amount": "Amount"
   };
 
   // columns to hide from UI (system/internal fields)
   const hiddenColumns = [
     "QcId",
     "Gipid",
     "QcSeqId",
     "PoId",
     "itemslno",
     "Itemid",
     "UnitPrice",
     "Amount"
   ];
 
   // visible header list computed from headers (keeps order)
   const visibleHeaders = headers.filter(h => !hiddenColumns.includes(h));
 
   useEffect(() => {
     fetchQCDetails();
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);
 
   // ✅ Function to handle Save action for each part
   const handleSave = async (item, index) => {
     try {
       const databaseKey = sessionStorage.getItem("databaseKey");
       const bodyParams = {
         QcSeqId: item.QcSeqId,
         Accepted: parseFloat(item.acceptedQty) || 0,
         RejQty: parseFloat(item.qcRejQty) || 0,
       };
 
       const response = await api.UpdateInspectionQty_API(bodyParams, databaseKey);
 
       // Mark as saved: button turns green
       const updatedParts = [...parts];
       updatedParts[index].updated = false;
       setParts(updatedParts);
 
       // ✅ Show success message
       alert("✅ Qty Updated");
     } catch (error) {
       console.error("❌ Save failed:", error);
       alert("❌ Failed to save! Check console for details.");
     }
   };
 
 
   const handleQtyChange = (value, index, field) => {
     const updatedParts = [...parts];
     updatedParts[index][field] = value;
     updatedParts[index].updated = true; // mark as unsaved → button turns red
     setParts(updatedParts);
   };
 
 
 
   // fetch initial QC details including users/checkers
   const fetchQCDetails = async () => {
     try {
       if (!Gipid) return;
       setLoading(true);
       const UserName = sessionStorage.getItem("userName");
       const databaseKey = sessionStorage.getItem("databaseKey");
 
       const response = await api.DashBoardQcEntryList_API(Gipid, UserName, databaseKey);
       const result = response?.DashBoardQcEntryList_API_APIjs;
 
       if (result?.Table && result.Table.length > 0) {
         const header = result.Table[0];
         const details = result.Table1 || [];
         const checkedUsers = result.Table2 || [];
         const supervisorUsers = result.Table4 || []; // may contain CheckedByName or supervisor info
 
         // header fields
         setRecord({
           gipNo: header.GIPNo,
           Gipid: header.Gipid,
           GRNId: header.GRNId,
           gipDate: header.GIPDate?.split("T")[0],
           supName: header.VName,
           Vid: header.Vid,
           dcNo: header.DCNo,
           dcDate: header.DCDate?.split("T")[0],
         });
 
         setQcNo(header.QcNo || "");
         setQcDate(header.QCDate?.split("T")[0] || "");
         setPreparedBy(header.PreparedBy ? { value: header.PreparedBy, label: header.PreparedBy } : null);
         setCheckedBy(header.CheckedBy ? { value: header.CheckedBy, label: header.CheckedBy } : null);
         // if header has Supervisor and SupervisorId use them
         if (header.Supervisor || header.SupervisorId) {
           setSupervisor({
             value: header.SupervisorId || header.Supervisor,
             label: header.Supervisor || String(header.SupervisorId),
           });
         }
 
         // Build users dropdown from result.Table2 if possible
         const userOptions = checkedUsers.map((u) => {
           const value = u.CheckedById || u.CheckedByName || u.CheckedBy;
           const label = u.CheckedByName || u.CheckedBy || String(value);
           return { value, label };
         });
         setUsers(userOptions);
 
 
 
 
         const supervisorOptions = supervisorUsers.map((u) => {
           const value = u.SupervisorId || u.Supervisor;
           const label = u.Supervisor || String(value);
           return { value, label };
         });
         setSupervisors(supervisorOptions);
 
         // Also build supervisors options from same array (dedupe by value)
         // const supMap = {};
         // userOptions.forEach(o => {
         //   if (o.value != null && !supMap[o.value]) supMap[o.value] = o;
         // });
         // const supOptions = Object.values(supMap);
         // setSupervisors(supOptions);
 
         // Map details -> parts (keep hidden ids in structure)
         const mappedParts = details.map((p, i) => {
           const received = parseFloat(p.ReceivedQty || 0);
           const accepted = p.AcceptedQty ? parseFloat(p.AcceptedQty) : received;
           const qcRej = received - accepted;
           const UnitPrice = parseFloat(p.UnitPrice || 0);
           const Amount = parseFloat(p.Amount || 0);
 
           return {
             QcId: p.QcId,
             Gipid: p.Gipid,
             QcSeqId: p.QcSeqId,
             slno: i + 1,
             PoId: p.PoId,
             poNo: p.Pono,
             poDate: p.PODate?.split("T")[0],
             itemslno: p.itemslno,
             Itemid: p.Itemid,
             partNo: p.PartNo,
             description: p.PartDesc,
             uom: p.Uom,
             Materialtype: p.Materialtype || "",
             mcNo: p.Mcno || "",
             HeatNo: p.HeatNo || "",
             receivedQty: received.toFixed(3),
             acceptedQty: accepted.toFixed(3),
             qcRejQty: qcRej.toFixed(3),
             UnitPrice,
             Amount,
             assetInfo: p.AssetInfo || "",
             uploadedFile: p.Upload || "",
             rejectionDetails: p.RejectionDetails || null,
             updated: false,
             showErrorDialog: false,
             errorMessage: "",
           };
         });
 
         // Map rejection details from Table3
         const rejDetails = result.Table3 || [];
         const rejMap = {};
         rejDetails.forEach(r => {
           const key = `${r.QcSeqId}-${r.itemid}`;
           if (!rejMap[key]) rejMap[key] = [];
           rejMap[key].push({
             type: r.RejType,
             qty: r.RejQty,
             remarks: r.Remaks
           });
         });
 
         // Update parts with rejectionDetails
         const updatedParts = mappedParts.map(p => {
           const key = `${p.QcSeqId}-${p.Itemid}`;
           return {
             ...p,
             rejectionDetails: rejMap[key] || null
           };
         });
 
         setParts(updatedParts);
 
         // Fetch rejection types
         try {
           const rejResponse = await api.QcrejectionType_API(UserName, databaseKey);
           const rejResult = rejResponse?.QcrejectionType_API_APIjs;
           const rejOptions = rejResult?.Table?.map(item => ({
             value: item.RejectionType,
             label: item.RejectionType
           })) || [];
           setRejectionTypesOptions(rejOptions);
         } catch (rejErr) {
           console.error("❌ Error fetching rejection types:", rejErr);
         }
       }
     } catch (err) {
       console.error("❌ Error fetching QC details:", err);
     } finally {
       setLoading(false);
     }
   };
 
   const to3 = (val) => {
     const n = parseFloat(val || 0);
     if (isNaN(n)) return "0.000";
     return n.toFixed(3);
   };
 
   // handle changes to accepted qty via inline input (keeps behaviour you had)
   const handleAcceptedQtyChange = (index, value) => {
     const updated = [...parts];
     const received = parseFloat(updated[index].receivedQty) || 0;
     const accepted = parseFloat(value) || 0;
 
     if (accepted > received) {
       alert("❌ Accepted Qty cannot be greater than Received Qty!");
       return;
     }
 
     updated[index].acceptedQty = to3(accepted);
     updated[index].qcRejQty = to3(received - accepted);
     updated[index].updated = true;
     setParts(updated);
 
     setTimeout(() => {
       updated[index].updated = false;
       setParts([...updated]);
     }, 1500);
   };
 
   const handleFileUpload = async (file, index) => {
     if (!file) return;
     try {
       const formData = new FormData();
       formData.append("file", file);
       formData.append("partNo", parts[index].partNo);
       formData.append("gipNo", record.gipNo);
 
       const res = await api.postForm("/UploadQCFile", formData);
 
       if (res.status === 200) {
         const updated = [...parts];
         updated[index].uploadedFile = res.data.filePath || file.name;
         setParts(updated);
         alert("✅ File uploaded successfully!");
       } else {
         alert("❌ Upload failed!");
       }
     } catch (err) {
       console.error(err);
       alert("❌ File upload error!");
     }
   };
 
   const handleDownload = (filePath) => {
     if (!filePath) return alert("No file to download");
     // keep same host pattern as original code — adjust if needed
     window.open(`http://localhost:63994/${filePath}`, "_blank");
   };
 
   // fetch assets for a specific part and set defaults
   const fetchAssetsForPart = async (itemId) => {
     try {
       const userName = sessionStorage.getItem("userName");
       const databaseKey = sessionStorage.getItem("databaseKey");
 
       const res = await api.GetQcAssetList_API(Gipid, itemId, userName, databaseKey);
       const result = res?.GetQcAssetList_API_APIjs;
       const assetData = result?.Table || [];
 
       console.log("🔍 Debug: Asset data received:", assetData);
 
       // Group assets by type
       const assets = [];
       const mandrels = [];
       const dies = [];
 
       assetData.forEach((a) => {
         const type = a.AssetType?.toLowerCase();
         const option = { value: a.AssetId, label: `${a.AssetNo} - ${a.AssetName}` };
         if (type?.includes('die') || type?.includes('dies')) {
           dies.push(option);
         } else if (type?.includes('mandrel')) {
           mandrels.push(option);
         } else {
           assets.push(option);
         }
       });
 
       console.log("🔍 Debug: Assets:", assets.length, "Mandrels:", mandrels.length, "Dies:", dies.length);
 
       setAssetOptions(assets);
       setMandrelOptions(mandrels);
       setDieOptions(dies);
 
       // Set selected to first option or N/A
       setSelectedAsset(assets.length > 0 ? assets[0] : { value: 0, label: "N/A" });
       setSelectedMandrel(mandrels.length > 0 ? mandrels[0] : { value: 0, label: "N/A" });
       setSelectedDie(dies.length > 0 ? dies[0] : { value: 0, label: "N/A" });
     } catch (err) {
       console.error("❌ Error fetching Asset list:", err);
     }
   };
 
   // Open asset verify popup and set selected asset/mandrel/die based on fetched defaults
   const openVerifyPopup = async (index) => {
     const current = parts[index];
     setSelectedPartIndex(index);
     setShowPopup(true);
 
     // Fetch assets for this part and set defaults
     await fetchAssetsForPart(current.Itemid);
   };
 
   const handleSavePopup = async () => {
     try {
       const asset = selectedAsset || { value: 0, label: "N/A" };
       const mandrel = selectedMandrel || { value: 0, label: "N/A" };
       const die = selectedDie || { value: 0, label: "N/A" };
 
       const updated = [...parts];
       if (selectedPartIndex != null) {
         updated[selectedPartIndex].assetInfo = `${asset.label}, ${mandrel.label}, ${die.label}`;
         setParts(updated);
       }
 
       const databaseKey = sessionStorage.getItem("databaseKey");
       const userName = sessionStorage.getItem("userName");
 
       const payload = {
         QcSeqid: updated[selectedPartIndex]?.QcSeqId, // fixed name
         MandrelId: mandrel.value || 0,
         MandrelNo: mandrel.label || "N/A",
         ToolId: asset.value || 0,
         ToolNo: asset.label || "N/A",
         DieId: die.value || 0,
         DieNo: die.label || "N/A",
       };
 
       console.log("Sending payload:", payload);
 
       const res = await api.UpdateInspectionTools_API(payload, databaseKey, userName);
 
       if (res?.data?.toString().toLowerCase().includes("success")) {
         alert("✅ Tools updated successfully!");
       } else {
         alert("✅ Tools updated successfully!");
       }
     } catch (error) {
       console.error("Error updating inspection tools:", error);
       alert("❌ Error updating inspection tools!");
     } finally {
       setShowPopup(false);
     }
   };
 
 
   const openRejectionPopup = (index) => {
     const current = parts[index];
 
     const qcRejQty = parseFloat(current.qcRejQty || 0);
 
     // ✅ Only open when Rej.Qty > 0
     if (!qcRejQty || qcRejQty <= 0) {
       alert("Rejection details can be added only if Rej.Qty (qcRejQty) is greater than 0.");
       return;
     }
 
     // ✅ Store this for validation inside modal
     setQcRejQtyLimit(qcRejQty);
     setRejIndex(index);
 
     // Normalize existing rejectionDetails into array of rows
     let rows = [];
     if (current.rejectionDetails) {
       if (Array.isArray(current.rejectionDetails)) {
         rows = current.rejectionDetails.map((r, i) => ({
           id: `${Date.now()}-${i}`,
           qty: r.qty || r.Qty || "",
           remarks: r.remarks || r.Remarks || "",
           type: r.type
             ? { value: r.type, label: r.type }
             : r.typeObj || null,
         }));
       } else if (typeof current.rejectionDetails === "object") {
         rows = [
           {
             id: `${Date.now()}-0`,
             qty: current.rejectionDetails.qty || "",
             remarks: current.rejectionDetails.remarks || "",
             type: current.rejectionDetails.type
               ? { value: current.rejectionDetails.type, label: current.rejectionDetails.type }
               : null,
           },
         ];
       } else {
         rows = [
           {
             id: `${Date.now()}-0`,
             qty: "",
             remarks: String(current.rejectionDetails || ""),
             type: null,
           },
         ];
       }
     } else {
       rows = [{ id: `${Date.now()}-0`, qty: "", remarks: "", type: null }];
     }
 
     setRejRows(rows);
     setShowRejPopup(true);
   };
 
   const addRejRow = () => {
     setRejRows(prev => [...prev, { id: `${Date.now()}`, qty: "", remarks: "", type: null }]);
   };
 
   const deleteRejRow = (id) => {
     setRejRows(prev => prev.filter(r => r.id !== id));
   };
 
   const updateRejRow = (id, field, value) => {
     setRejRows(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));
   };
 
   const handleSaveRejection = async () => {
     if (rejIndex == null) return;
 
     const currentPart = parts[rejIndex];
     const payload = {
       Sp_SqlAction: "INSERT",
       Gipid: currentPart.Gipid,
       QcSeqId: currentPart.QcSeqId,
       ItemTbl: rejRows.map(r => ({
         Qcid: currentPart.QcId,
         itemid: currentPart.Itemid,
         RejType: r.type?.value || r.type?.label || "",
         RejQty: parseFloat(r.qty) || 0,
         Remaks: r.remarks
       }))
     };
 
     try {
       await api.DMLQcRejectionDetails_API(payload);
 
       // Normalize stored structure as array of simple objects
       const normalized = rejRows.map(r => ({
         type: r.type?.value || (typeof r.type === "string" ? r.type : null),
         qty: r.qty === "" ? 0 : parseFloat(r.qty) || 0,
         remarks: r.remarks || ""
       }));
 
       const updated = [...parts];
       updated[rejIndex].rejectionDetails = normalized;
       setParts(updated);
       setShowRejPopup(false);
       alert("✅ Rejection details saved successfully!");
     } catch (error) {
       console.error("❌ Error saving rejection details:", error);
       alert("❌ Failed to save rejection details. Check console for details.");
     }
   };
 
   const handleFileChange = (e, index) => {
     const file = e.target.files[0];
     setFiles(prev => ({ ...prev, [index]: file }));
   };
 
   const handleInsert = async (index) => {
     const file = files[index];
     if (!file) {
       alert('Please select a file first.');
       return;
     }
     try {
       console.log('Starting to parse file for part index:', index);
       console.log('File details:', { name: file.name, size: file.size, type: file.type });
 
       // Read file as base64
       const fileContent = await new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result);
         reader.onerror = reject;
         reader.readAsDataURL(file);
       });
       console.log('File content loaded as base64');
 
       const { headData, itemData } = await parseExcelFile(file, index, parts);
       console.log('Parsed headData:', headData);
       console.log('Parsed itemData length:', itemData.length);
       console.log('First few itemData entries:', itemData.slice(0, 3));
       if (itemData.length === 0) {
         alert('No valid item data found in the file.');
         return;
       }
 
       // Construct payload for API
       const payload = {
         GrnNo: record.gipNo,
         GrnId: parts[index].Gipid,
         UserName: sessionStorage.getItem('userName'),
         GrnDate: record.gipDate,
         PartNo: headData.PartNo || parts[index].partNo,
         PartName: headData.PartName || '',
         LotQty: headData.LotQty,
         BatchNo: headData.BatchNo,
         InspectionQty: headData.InspectionQty,
         RejectionQty: headData.RejectionQty,
         SourceFile: file.name,
         FileContent: fileContent,
         Items: itemData,
         InspectionDate: new Date().toISOString(),
       };
 
       console.log('Payload for API:', payload);
 
       try {
         const qcSeqId = parts[index].QcSeqId || parts[index].Itemid;
         if (!qcSeqId && qcSeqId !== 0) {
           alert("❌ QcSeqId or Itemid is missing for this part. Cannot upload file.");
           return;
         }
 
         const formData = new FormData();
         formData.append("file", file);
         formData.append("QcSeqId", qcSeqId);
         formData.append("GipId", Gipid);
 
         console.log("🔍 Debug: Sending file upload request");
         console.log("🔍 Debug: QcSeqId:", qcSeqId);
         console.log("🔍 Debug: File name:", file.name);
         console.log("🔍 Debug: File size:", file.size);
 
         const res = await fetch("https://spflqc.revampaioffice.com/api/upload.php", {
  method: "POST",
  body: formData
});
         console.log("🔍 Debug: Fetch response status:", res.status);
         console.log("🔍 Debug: Fetch response ok:", res.ok);
 
         const result = await res.json();
         console.log("🔍 Debug: Server response:", result);
 
         if (result.success) {
           // update UI file reference
           const updated = [...parts];
           updated[index].uploadedFile = result.filePath;
           setParts(updated);
         } else {
           alert("❌ Failed to save file locally.");
         }
       } catch (error) {
         console.error("Error saving file:", error);
         alert("❌ Error saving file locally.");
       }
 
       const result = await api.uploadInspectionReport(payload);
       console.log('API Response:', result);
 
       alert(`✅ Success: ${result.message || 'Data uploaded successfully.'}`);
 
       // Optionally update the part with parsed data
       const item = itemData[0];
       const updatedParts = [...parts];
       updatedParts[index] = {
         ...updatedParts[index],
         description: item.Description || updatedParts[index].description,
         observations: item,
       };
       setParts(updatedParts);
 
     } catch (error) {
       console.error('Error inserting data:', error);
       alert('Error: ' + error.message);
     }
   };
 
   // Build payload & call StartInspectionEntry_API (same name you used)
   const handleUpdate = async () => {
     try {
       const databaseKey = sessionStorage.getItem("databaseKey");
       const UsrName = sessionStorage.getItem("userName");
       const QcRefNo = qcNo;
       const QcRefdate = qcDate;
       const QcById = checkedBy?.value || 0;
       const QcByName = checkedBy?.label || "";
       const SupervisorId = supervisor?.value || 0;
       const SupervisorName = supervisor?.label || "";
 
       if (!QcRefNo || !QcRefdate) {
         alert("❌ Please enter QC No and QC Date!");
         return;
       }
       if (!QcByName) {
         alert("❌ Please select Checked By!");
         return;
       }
 
       setLoading(true);
 
       // Build details array for API - include hidden ids where necessary
       const details = parts.map(p => ({
         QcId: p.QcId,
         Gipid: p.Gipid,
         QcSeqId: p.QcSeqId,
         PoId: p.PoId,
         Itemid: p.Itemid,
         ReceivedQty: p.receivedQty,
         AcceptedQty: p.acceptedQty,
         RejectionDetails: p.rejectionDetails,
         AssetInfo: p.assetInfo,
         Upload: p.uploadedFile
       }));
 
       const bodyParams = {
         Gipid,
         QcRefNo,
         QcRefdate,
         QcById,
         QcByName,
         SupervisorId,
         Supervisor: SupervisorName,
         UsrName,
         Details: details
       };
 
       const response = await api.StartInspectionEntry_API(bodyParams, databaseKey);
       console.log("✅ API Response:", response);
       alert("✅ QC Entry Updated Successfully!");
 
       // reload parts/headers after update
       await fetchQCDetails();
     } catch (error) {
       console.error("❌ Error updating inspection entry:", error);
       alert("❌ Error updating inspection entry. Please check console for details.");
     } finally {
       setLoading(false);
     }
   };
 
   const handleCancelPage = () => navigate(-1);
 
   // helper to render cell value safely
   const getCellValue = (item, accessor) => {
     if (!item) return "";
     const key = headerMap[accessor] || accessor;
     const v = item[key];
     if (key === "receivedQty" || key === "acceptedQty" || key === "qcRejQty" || key === "UnitPrice" || key === "Amount") {
       return to3(v);
     }
     // for rejectionDetails object, show summary
     if (key === "rejectionDetails") {
       if (!v) return "";
       if (typeof v === "object") {
         return v.remarks ? `${v.type || ""} (${v.qty || ""}) - ${v.remarks}` : `${v.type || ""} (${v.qty || ""})`;
       }
       return String(v);
     }
     return v ?? "";
   };
 
   return (
     <div className="p-4 bg-gray-50 min-h-screen">
       <h2 className="text-xl font-semibold text-gray-700 mb-3">QC Entry Finished</h2>
 
       <div className="gap-3 mb-4">
         <button onClick={handleCancelPage} className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600">
           Back
         </button>
       </div>
 
       {loading ? (
         <p className="text-gray-500">Loading QC details...</p>
       ) : (
         <>
           {/* Header section */}
           <div className="bg-white p-4 rounded-lg shadow mb-5 grid grid-cols-3 gap-3 text-sm">
             {[{ label: "GIP No", value: record.gipNo },
             { label: "GIP Date", value: record.gipDate, type: "date" },
             { label: "Supplier", value: record.supName },
             { label: "DC No", value: record.dcNo },
             { label: "DC Date", value: record.dcDate, type: "date" }].map((f, i) => (
               <div key={i}>
                 <label className="text-xs text-gray-600 block mb-0.5">{f.label}</label>
                 <input type={f.type || "text"} className="border p-1.5 text-sm w-full rounded bg-gray-50" value={f.value || ""} readOnly />
               </div>
             ))}
 
             <div>
               <label className="text-xs text-gray-600 block mb-0.5">QC No</label>
               <input type="text" className="border p-1.5 text-sm w-full rounded" value={qcNo} onChange={(e) => setQcNo(e.target.value)} />
             </div>
 
             <div>
               <label className="text-xs text-gray-600 block mb-0.5">QC Date</label>
               <input type="date" className="border p-1.5 text-sm w-full rounded" value={qcDate} onChange={(e) => setQcDate(e.target.value)} />
             </div>
 
             <div>
               <label className="text-xs text-gray-600 block mb-0.5">Prepared By</label>
               <input type="text" className="border p-1.5 text-sm w-full rounded bg-gray-100 cursor-not-allowed" value={preparedBy?.label || ""} readOnly />
             </div>
 
             <div>
  <label className="text-xs text-gray-600 block mb-0.5">Checked By</label>
  <Select
    options={users}
    value={checkedBy}
    onChange={setCheckedBy}
    placeholder="Select user"
    isDisabled={true} // ✅ makes it read-only
    styles={{
      control: (base, state) => ({
        ...base,
        minHeight: "28px",
        fontSize: "12px",
        backgroundColor: "#f9f9f9", // light gray for read-only
        cursor: "not-allowed",
        opacity: state.isDisabled ? 0.8 : 1,
      }),
    }}
  />
</div>
 
             <div>
  <label className="text-xs text-gray-600 block mb-0.5">Supervisor</label>
  <Select
    options={supervisors.length ? supervisors : users}
    value={supervisor}
    onChange={setSupervisor}
    placeholder="Select supervisor"
    isDisabled={true} // ✅ makes it read-only
    styles={{
      control: (base, state) => ({
        ...base,
        minHeight: "28px",
        fontSize: "12px",
        backgroundColor: "#f9f9f9", // subtle gray to show disabled
        cursor: "not-allowed",
        opacity: state.isDisabled ? 0.8 : 1,
      }),
    }}
  />
</div>
 
             <div />
            
           </div>
 
           {/* Parts Table */}
           <div className="bg-white rounded-lg shadow p-3 mb-5">
             <h3 className="text-base font-semibold text-gray-700 mb-2">QC Parts List</h3>
             
             <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
               <table className="w-full text-xs border-collapse">
                 <thead>
                   <tr className="bg-gray-100 text-gray-700">
                     {visibleHeaders.map((h) => (
                       <th key={h} className="border p-1.5 text-[11px]">{h}</th>
                     ))}
                   </tr>
                 </thead>
                 <tbody>
                   {parts.length > 0 ? (
                     parts.map((item, i) => (
                       <tr key={i} className="hover:bg-gray-50">
                         {visibleHeaders.map((h) => {
                           const accessor = headerMap[h];
 
                           // ✅ Accepted Qty Column
                           if (h === "Accepted") {
                             return (
                               <td key={h} className="border p-1.5 w-28 text-right">
                                 <div className="relative">
                                   <input
                                     type="number"
                                     step="0.001"
                                     min="0"
                                     max={item.receivedQty}
                                     value={item.acceptedQty}
                                     onChange={(e) => {
                                       let val = e.target.value;
                                       if (val === "" || /^[0-9]*\.?[0-9]*$/.test(val)) {
                                         const updated = [...parts];
                                         updated[i].acceptedQty = val;
 
                                         const received = parseFloat(updated[i].receivedQty) || 0;
                                         const accepted = parseFloat(val) || 0;
 
                                         updated[i].qcRejQty = (received - accepted).toFixed(3);
 
                                         if (accepted > received) {
                                           updated[i].showErrorDialog = true;
                                           updated[i].errorMessage = "❌ Accepted Qty cannot exceed Received Qty!";
                                         } else {
                                           updated[i].showErrorDialog = false;
                                           updated[i].errorMessage = "";
                                         }
 
                                         updated[i].updated = true; // mark as unsaved → button turns red
                                         setParts(updated);
                                       }
                                     }}
                                     onBlur={() => {
                                       const updated = [...parts];
                                       let val = parseFloat(updated[i].acceptedQty);
                                       if (isNaN(val)) val = 0;
 
                                       const received = parseFloat(updated[i].receivedQty);
                                       if (val > received) val = received;
 
                                       updated[i].acceptedQty = val.toFixed(3);
                                       updated[i].qcRejQty = (received - val).toFixed(3);
 
                                       // Clear error display, but do NOT mark as saved
                                       updated[i].showErrorDialog = false;
                                       updated[i].errorMessage = "";
 
                                       // ❌ Remove this line:
                                       // updated[i].updated = false;
 
                                       setParts(updated);
                                     }}
                                     className={`border w-full text-right text-[12px] rounded px-2 py-1 ${item.showErrorDialog ? "border-red-500 bg-red-50" : "border-gray-300 bg-white"
                                       }`}
                                   />
 
                                   {item.showErrorDialog && (
                                     <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full bg-red-600 text-white text-xs rounded px-2 py-1 shadow-lg z-10">
                                       {item.errorMessage}
                                     </div>
                                   )}
                                 </div>
                               </td>
                             );
                           }
 
                           // ✅ Rejected Qty Column
                           if (h === "Rej Qty") {
                             return (
                               <td
                                 key={h}
                                 className={`border p-1.5 text-right font-semibold ${parseFloat(item.qcRejQty) > 0
                                     ? "text-red-600"
                                     : "text-green-600"
                                   }`}
                               >
                                 {to3(item.qcRejQty)}
                               </td>
                             );
                           }
 
                           // // ✅ Download Button
                           // if (h === "Download") {
                           //   return (
                           //     <td key={h} className="border p-1.5 text-center">
                           //       <button
                           //         onClick={() => handleDownload(item.uploadedFile)}
                           //         className="text-blue-600 hover:underline"
                           //       >
                           //         Download
                           //       </button>
                           //     </td>
                           //   );
                           // }
 
                           // ✅ Upload Button
                           if (h === "Download") {
                             return (
                               <td key={h} className="border p-2 text-center flex flex-col items-center space-y-1">
                                
                                 <button onClick={() => handleInsert(i)} className="bg-green-500 text-white px-3 py-1 rounded text-xs hover:bg-green-600 transition">Download</button>
                               </td>
                             );
                           }
 
                           // ✅ Asset Info Button
                           if (h === "Asset Info") {
                             return (
                               <td key={h} className="border p-1.5 text-center">
                                 <button
                                   onClick={() => openVerifyPopup(i)}
                                   className={`font-medium ${item.assetInfo
                                       ? "text-green-600 hover:text-green-700"
                                       : "text-blue-600 hover:text-blue-700"
                                     }`}
                                 >
                                   {item.assetInfo ? "Verified" : "Not Verified"}
                                 </button>
                               </td>
                             );
                           }
 
                           // ✅ Rejection Details Button
                           if (h === "Rejection Details") {
                             return (
                               <td key={h} className="border p-1.5 text-center">
                                 <button
                                   onClick={() => openRejectionPopup(i)}
                                   className={`px-3 py-1 rounded text-xs font-medium ${item.rejectionDetails
                                       ? "bg-yellow-500 text-white hover:bg-yellow-600"
                                       : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                                     }`}
                                 >
                                   {item.rejectionDetails ? "View" : "Add"}
                                 </button>
                               </td>
                             );
                           }
 
                           
 
                           // ✅ Default Column
                           return (
                             <td key={h} className="border p-1.5 text-center">
                               {getCellValue(item, h)}
                             </td>
                           );
                         })}
                       </tr>
                     ))
                   ) : (
                     <tr>
                       <td
                         colSpan={visibleHeaders.length}
                         className="text-center p-3 text-gray-500"
                       >
                         No parts found
                       </td>
                     </tr>
                   )}
                 </tbody>
               </table>
             </div>
           </div>
 
           {/* Asset Verify Popup */}
           {showPopup && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
               <div className="bg-white rounded-lg shadow-2xl p-5 w-[380px]">
                 <h3 className="text-base font-semibold text-gray-700 mb-3 text-center">Verify / Edit Asset Info</h3>
                 <div className="space-y-2 text-sm">
                   <label className="text-xs text-gray-600 mb-0.5 block">Asset</label>
                   {assetOptions.length > 1 ? (
                     <Select
                       options={assetOptions}
                       value={selectedAsset}
                       onChange={setSelectedAsset}
                       placeholder="Select Asset"
                       styles={{
                         control: (base) => ({
                           ...base,
                           minHeight: "28px",
                           fontSize: "12px",
                         }),
                       }}
                     />
                   ) : (
                     <input type="text" className="border rounded w-full p-1 text-sm bg-gray-100" value={selectedAsset?.label || "N/A"} readOnly />
                   )}
                   <label className="text-xs text-gray-600 mb-0.5 block">Mandrel</label>
                   {mandrelOptions.length > 1 ? (
                     <Select
                       options={mandrelOptions}
                       value={selectedMandrel}
                       onChange={setSelectedMandrel}
                       placeholder="Select Mandrel"
                       styles={{
                         control: (base) => ({
                           ...base,
                           minHeight: "28px",
                           fontSize: "12px",
                         }),
                       }}
                     />
                   ) : (
                     <input type="text" className="border rounded w-full p-1 text-sm bg-gray-100" value={selectedMandrel?.label || "N/A"} readOnly />
                   )}
                   <label className="text-xs text-gray-600 mb-0.5 block">Die</label>
                   {dieOptions.length > 1 ? (
                     <Select
                       options={dieOptions}
                       value={selectedDie}
                       onChange={setSelectedDie}
                       placeholder="Select Die"
                       styles={{
                         control: (base) => ({
                           ...base,
                           minHeight: "28px",
                           fontSize: "12px",
                         }),
                       }}
                     />
                   ) : (
                     <input type="text" className="border rounded w-full p-1 text-sm bg-gray-100" value={selectedDie?.label || "N/A"} readOnly />
                   )}
                 </div>
                 <div className="flex justify-end gap-3 mt-4">
                   <button onClick={() => setShowPopup(false)} className="bg-red-500 text-white px-3 py-1.5 rounded hover:bg-red-600">Cancel</button>
                   <button onClick={handleSavePopup} className="bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700">Save</button>
                 </div>
               </div>
             </div>
           )}
 
           {/* Rejection Popup (new table-style modal) */}
           {showRejPopup && (
             <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-start pt-20 z-50 ">
               <div className="bg-white p-6 rounded shadow-xl w-[700px] max-h-[2000vh] overflow-auto">
                 <div className="flex items-center justify-between mb-3">
                   <h3 className="text-base font-semibold text-gray-700">Rejection Details</h3>
                   <div className="text-sm">
                     <button onClick={addRejRow} className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600">Add</button>
                   </div>
                 </div>
 
                 <div className="text-xs text-gray-600 mb-2">Click <span className="font-medium">Delete</span> to remove a row. Use the spin control for quantities.</div>
 
                 <div className="overflow-auto">
                   <table className="w-full table-fixed text-sm border-collapse">
                     <thead>
                       <tr className="bg-gray-100 text-gray-700">
                         <th className="w-16 border p-2 text-left">Action</th>
                         <th className="w-28 border p-2 text-right">Rej.Qty</th>
                         <th className="border p-2 text-left">Remarks</th>
                         <th className="w-40 border p-2 text-left">Rejection Type</th>
                       </tr>
                     </thead>
                     <tbody>
                       {rejRows.map((r) => (
                         <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                           <td className="border p-2 align-top">
                             <button
                               onClick={() => deleteRejRow(r.id)}
                               className="text-blue-600 hover:underline text-sm"
                             >
                               Delete
                             </button>
                           </td>
 
                           <td className="border p-2 align-top text-right">
                             <input
                               type="number"
                               step="0.001"
                               min="0"
                               max={qcRejQtyLimit}
                               className="border rounded px-2 py-1 w-full text-right"
                               value={r.qty}
                               onChange={(e) => {
                                 const val = e.target.value;
                                 const num = parseFloat(val) || 0;
 
                                 // Basic format validation
                                 if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                                   // Compute current total excluding this row
                                   const otherTotal = rejRows
                                     .filter(x => x.id !== r.id)
                                     .reduce((sum, x) => sum + (parseFloat(x.qty) || 0), 0);
 
                                   // Check total limit
                                   if (otherTotal + num > qcRejQtyLimit) {
                                     alert(
                                       `Total Rej.Qty (${otherTotal + num}) cannot exceed QC Rej.Qty (${qcRejQtyLimit}).`
                                     );
                                     // Auto adjust to remaining qty
                                     const remaining = Math.max(qcRejQtyLimit - otherTotal, 0);
                                     updateRejRow(r.id, "qty", remaining.toString());
                                   } else {
                                     updateRejRow(r.id, "qty", val);
                                   }
                                 }
                               }}
                             />
                           </td>
 
 
                           <td className="border p-2 align-top">
                             <input
                               type="text"
                               className="border rounded px-2 py-1 w-full"
                               value={r.remarks}
                               onChange={(e) => updateRejRow(r.id, "remarks", e.target.value)}
                             />
                           </td>
 
                           <td className="border p-2 align-top">
                             <Select
                               options={rejectionTypesOptions}
                               value={r.type}
                               onChange={(val) => updateRejRow(r.id, "type", val)}
                               placeholder="Select type"
                               styles={{ control: base => ({ ...base, minHeight: "30px", fontSize: "13px" }) }}
                             />
                           </td>
                         </tr>
                       ))}
 
                       {rejRows.length === 0 && (
                         <tr>
                           <td colSpan={4} className="p-4 text-center text-gray-500">No rejection rows. Click Add to create one.</td>
                         </tr>
                       )}
                     </tbody>
                   </table>
                 </div>
 
                 <div className="flex justify-end gap-3 mt-4">
                   <button onClick={() => setShowRejPopup(false)} className="bg-red-500 text-white px-3 py-1.5 rounded hover:bg-red-600">Cancel</button>
                   <button onClick={handleSaveRejection} className="bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700">Save</button>
                 </div>
               </div>
             </div>
           )}
         </>
       )}
     </div>
   );
 }
 
 